// src/components/AssessmentEngine.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronRight, ChevronLeft, Save, Loader2, AlertCircle, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
// --- ADDED CRITICAL IMPORT ---
import { CLINICAL_MODULES } from '../data/assessment-config'; 

const AssessmentEngine = ({ config, onComplete, onStepChange }) => {
    const [activeSections, setActiveSections] = useState([]);
    const [activeQuestionCoords, setActiveQuestionCoords] = useState({ sectionIdx: 0, questionIdx: 0 });
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sortableLists, setSortableLists] = useState({});

    const lastReportedIdRef = useRef(null);

    useEffect(() => {
        if (config) {
            setActiveSections([...config.screener]);
            const initialLists = {};
            config.screener.forEach(section => {
                section.questions.forEach(q => {
                    if (q.type === 'drag_sort' && q.options) {
                        initialLists[q.id] = [...q.options];
                    }
                });
            });
            setSortableLists(initialLists);
        }
    }, [config]);

    const flatQuestionList = useMemo(() => {
        let list = [];
        activeSections.forEach((sec, sIdx) => {
            sec.questions.forEach((q, qIdx) => {
                list.push({ ...q, sectionId: sec.id, sectionTitle: sec.title, sectionDescription: sec.description || '', sIdx, qIdx });
            });
        });
        return list;
    }, [activeSections]);

    const currentFlatIndex = flatQuestionList.findIndex(
        item => item.sIdx === activeQuestionCoords.sectionIdx && item.qIdx === activeQuestionCoords.questionIdx
    );
    const currentQuestion = flatQuestionList[currentFlatIndex];

    useEffect(() => {
        if (currentQuestion && onStepChange) {
            if (lastReportedIdRef.current !== currentQuestion.id) {
                lastReportedIdRef.current = currentQuestion.id;
                onStepChange({
                    text: currentQuestion.text,
                    type: currentQuestion.type,
                    progress: ((currentFlatIndex + 1) / flatQuestionList.length) * 100
                });
            }
        }
    }, [currentQuestion, currentFlatIndex, flatQuestionList.length, onStepChange]);

    const isCurrentQuestionAnswered = () => {
        if (!currentQuestion) return false;
        const val = answers[currentQuestion.id];
        if (val === undefined || val === "") return false;
        return true;
    };

    // The four slider ranges (0-24 / 25-49 / 50-74 / 75-100) are fixed; what they
    // are CALLED is per question. "Struggling / Managing / Performing / Thriving"
    // suits "how is your focus" and reads oddly on "I know what foods I need", so a
    // question may carry its own four labels (`rangeLabels`, set in the builder).
    // Display only: the stored answer is the 0-100 number either way.
    const DEFAULT_RANGE_LABELS = ["Struggling", "Managing", "Performing", "Thriving"];
    const rangeLabelsFor = (question) => {
        const custom = question?.rangeLabels;
        return (Array.isArray(custom) && custom.length === 4 && custom.every(l => typeof l === "string" && l.trim()))
            ? custom
            : DEFAULT_RANGE_LABELS;
    };
    const getSliderLabel = (val, question) => {
        const labels = rangeLabelsFor(question);
        if (val < 25) return { text: labels[0], color: "text-red-600" };
        if (val < 50) return { text: labels[1], color: "text-orange-500" };
        if (val < 75) return { text: labels[2], color: "text-yellow-600" };
        return { text: labels[3], color: "text-green-600" };
    };

    // --- REFACTORED: handleAnswer now supports local and global modules ---
    const handleAnswer = (questionId, value, triggerConfig) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));

        if (triggerConfig) {
            const isTriggered = triggerConfig.operator === '<' 
                ? value < triggerConfig.threshold 
                : value >= triggerConfig.threshold;

            if (isTriggered) {
                const moduleKey = triggerConfig.module;
                
                // FIXED: Look in local config first, then fall back to the global dictionary
                const moduleToAdd = (config.modules && config.modules[moduleKey]) || CLINICAL_MODULES[moduleKey];

                if (moduleToAdd && !activeSections.find(s => s.id === moduleToAdd.id)) {
                    setActiveSections(prev => [...prev, moduleToAdd]);
                }
            }
        }
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const qId = result.source.droppableId;
        const items = Array.from(sortableLists[qId]);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setSortableLists(prev => ({ ...prev, [qId]: items }));
        handleAnswer(qId, items, null); 
    };

    const handleNext = () => {
        if (!isCurrentQuestionAnswered()) {
            alert("Please answer the question before moving forward.");
            return;
        }
        const currentSection = activeSections[activeQuestionCoords.sectionIdx];
        if (activeQuestionCoords.questionIdx < currentSection.questions.length - 1) {
            setActiveQuestionCoords(prev => ({ ...prev, questionIdx: prev.questionIdx + 1 }));
        } else if (activeQuestionCoords.sectionIdx < activeSections.length - 1) {
            setActiveQuestionCoords({ sectionIdx: activeQuestionCoords.sectionIdx + 1, questionIdx: 0 });
        } else {
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (activeQuestionCoords.questionIdx > 0) {
            setActiveQuestionCoords(prev => ({ ...prev, questionIdx: prev.questionIdx - 1 }));
        } else if (activeQuestionCoords.sectionIdx > 0) {
            const prevSection = activeSections[activeQuestionCoords.sectionIdx - 1];
            setActiveQuestionCoords({ sectionIdx: activeQuestionCoords.sectionIdx - 1, questionIdx: prevSection.questions.length - 1 });
        }
    };

    const handleSubmit = async () => {
        if (!isCurrentQuestionAnswered()) return alert("Please answer the final question.");
        setIsSubmitting(true);
        onComplete({ answers });
        setIsSubmitting(false);
    };

    const getLikertOptions = (question) => {
        if (question.options && question.options.length > 0) return question.options;
        if (question.scaleLabels && Array.isArray(question.scaleLabels)) return question.scaleLabels;

        if (question.type === 'scale_0_3' || question.type === 'scale_frequency') 
            return ['Not at all', 'Several days', 'More than half', 'Nearly every day'];
        
        if (question.type === 'scale_0_4') 
            return ['Never', 'Rarely', 'Sometimes', 'Often', 'Very Often'];
            
        if (question.type === 'scale_0_5')
            return ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree', 'Very Strongly Agree'];

        return ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'];
    };

    // The value a Likert / select / yes-no option carries (shared by taps and keys).
    const optionValue = (question, label, idx) => {
        if (question.type === 'yes_no') {
            const manual = question.useManualScoring && question.manualValues && question.manualValues[label];
            return manual !== undefined && manual !== null ? manual : (label === 'Yes' ? 1 : 0);
        }
        if (question.type === 'select') return label;
        if (question.useManualScoring && question.manualValues && question.manualValues[label] !== undefined) return question.manualValues[label];
        return idx;
    };
    const optionLabels = (question) => (question.type === 'yes_no' ? ['Yes', 'No'] : getLikertOptions(question));

    // --- TAP TO ADVANCE -------------------------------------------------------
    // Choosing an answer moves on after a short beat; tapping the chosen answer
    // again, or Back, stays. Sliders, text and sorting keep the Next button, and
    // the last answer always waits for Complete.
    const ADVANCE_MS = 650;
    const [pendingAdvance, setPendingAdvance] = useState(null); // { id, at }
    const isLast = currentFlatIndex >= flatQuestionList.length - 1;
    const answerAndAdvance = (question, val) => {
        if (pendingAdvance && pendingAdvance.id === question.id && answers[question.id] === val) {
            setPendingAdvance(null);
            return;
        }
        handleAnswer(question.id, val, question.trigger);
        setPendingAdvance({ id: question.id, at: Date.now() });
    };
    const goBack = () => { setPendingAdvance(null); handleBack(); };
    useEffect(() => {
        if (!pendingAdvance) return undefined;
        if (!currentQuestion || pendingAdvance.id !== currentQuestion.id) { setPendingAdvance(null); return undefined; }
        if (currentFlatIndex >= flatQuestionList.length - 1) return undefined;
        const t = setTimeout(() => { setPendingAdvance(null); handleNext(); }, ADVANCE_MS);
        return () => clearTimeout(t);
    }, [pendingAdvance]); // eslint-disable-line react-hooks/exhaustive-deps

    // A sort question starts answered with the order shown: dragging is optional,
    // and Next no longer waits for a drag that the member may not want to make.
    useEffect(() => {
        if (!currentQuestion || currentQuestion.type !== 'drag_sort') return;
        if (answers[currentQuestion.id] !== undefined) return;
        const list = sortableLists[currentQuestion.id];
        if (Array.isArray(list) && list.length) setAnswers(prev => ({ ...prev, [currentQuestion.id]: [...list] }));
    }, [currentQuestion && currentQuestion.id, sortableLists]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- A COVER FOR EACH PART ------------------------------------------------
    // When a new part begins (the first one comes straight after the start
    // screen, so it needs none), a short cover carries the part's stem once,
    // large, with its count and time. Going back and forward again does not
    // re-show it.
    const [cover, setCover] = useState(null);
    const coveredRef = useRef(new Set());
    useEffect(() => {
        if (!currentQuestion) return;
        if (currentQuestion.qIdx === 0 && currentQuestion.sIdx > 0 && !coveredRef.current.has(currentQuestion.sIdx)) setCover(currentQuestion.sIdx);
    }, [currentQuestion && currentQuestion.sectionId, currentQuestion && currentQuestion.qIdx]); // eslint-disable-line react-hooks/exhaustive-deps
    const dismissCover = () => { if (cover !== null) coveredRef.current.add(cover); setCover(null); };
    const minutesFor = (n) => Math.max(1, Math.round((n * 5) / 60));

    // --- KEYS (desktop): 1-9 pick an option, Enter moves on --------------------
    useEffect(() => {
        const onKey = (e) => {
            if (!currentQuestion) return;
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'Enter') {
                if (cover !== null) { dismissCover(); return; }
                if (!isCurrentQuestionAnswered()) return;
                if (currentFlatIndex >= flatQuestionList.length - 1) handleSubmit(); else { setPendingAdvance(null); handleNext(); }
                return;
            }
            if (cover !== null) return;
            const n = parseInt(e.key, 10);
            const tappable = ['scale_frequency', 'scale_0_3', 'scale_0_4', 'scale_0_5', 'select', 'likert_4', 'yes_no'].includes(currentQuestion.type);
            if (!tappable || !(n >= 1 && n <= 9)) return;
            const labels = optionLabels(currentQuestion);
            if (n > labels.length) return;
            answerAndAdvance(currentQuestion, optionValue(currentQuestion, labels[n - 1], n - 1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }); // every render: the handler reads the latest answers and position

    if (!config || !currentQuestion) return <div className="p-8 text-center text-slate-400">Loading...</div>;
    
    const isButtonGrid = ['scale_frequency', 'scale_0_3', 'scale_0_4', 'scale_0_5', 'select', 'likert_4'].includes(currentQuestion.type);
    const section = activeSections[activeQuestionCoords.sectionIdx] || { questions: [] };
    const sectionProgress = ((activeQuestionCoords.questionIdx + 1) / Math.max(1, section.questions.length)) * 100;
    const coverSection = cover !== null ? activeSections[cover] : null;
    const coverIsExtra = cover !== null && cover >= (config.screener || []).length;
    
    const sliderValue = answers[currentQuestion.id] ?? 50;

    return (
        <div className="max-w-3xl mx-auto h-full flex flex-col px-4 pt-4 pb-0"> 
            <div className="mb-4 shrink-0">
                {/* Progress by part: one segment per part, done ones filled, the
                    current one in the accent colour filling as its questions go. */}
                <div className="flex justify-between items-end mb-2 gap-3">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest truncate">Part {activeQuestionCoords.sectionIdx + 1} of {activeSections.length} · {currentQuestion.sectionTitle}</span>
                    <span className="text-[10px] font-bold text-slate-400 tabular-nums shrink-0">{currentFlatIndex + 1} of {flatQuestionList.length}</span>
                </div>
                <div className="flex gap-1">
                    {activeSections.map((s, i) => (
                        <div key={s.id} className={`h-1.5 flex-1 rounded-full overflow-hidden ${i < activeQuestionCoords.sectionIdx ? 'bg-brand-primary' : i === activeQuestionCoords.sectionIdx ? 'bg-[var(--brand-accent)]' : 'bg-slate-200'}`}>
                            {i === activeQuestionCoords.sectionIdx && (
                                <div className="h-full bg-brand-primary rounded-full transition-all duration-500 ease-out" style={{ width: `${sectionProgress}%` }}></div>
                            )}
                        </div>
                    ))}
                </div>
                {/* The section's own framing line ("Think about the last two weeks",
                    or an instrument's stem). A quiet caption, on every question of
                    the section, so the frame is still there on question four. */}
                {currentQuestion.sectionDescription && (
                    <p className="text-[11px] font-medium text-slate-500 mt-2 leading-snug">{currentQuestion.sectionDescription}</p>
                )}
            </div>

            {cover !== null && coverSection ? (
            <div className="bg-white rounded-t-2xl shadow-lg border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0 animate-in fade-in duration-300">
                <div className="p-6 md:p-10 overflow-y-auto flex-1 custom-scrollbar flex flex-col items-center justify-center text-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-[var(--brand-highlight)] text-brand-primary flex items-center justify-center font-black text-lg">{cover + 1}</div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Part {cover + 1} of {activeSections.length} · {coverSection.title}</p>
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 leading-snug max-w-md text-balance">{coverSection.description || coverSection.title}</h2>
                    <p className="text-sm text-slate-500 max-w-sm leading-relaxed">
                        {(coverSection.questions || []).length} questions · about {minutesFor((coverSection.questions || []).length)} min.{' '}
                        {coverIsExtra ? 'A few more on this: your answers so far point here.' : 'Your answers stay with you.'}
                    </p>
                </div>
                <div className="p-4 bg-white border-t border-slate-100 shrink-0">
                    <button onClick={dismissCover} className="w-full py-3.5 bg-brand-primary text-white rounded-xl font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-md">
                        Begin <ChevronRight size={16} />
                    </button>
                </div>
            </div>
            ) : (
            <div className="bg-white rounded-t-2xl shadow-lg border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="p-4 md:p-8 overflow-y-auto flex-1 custom-scrollbar overscroll-contain">
                    {activeQuestionCoords.sectionIdx >= config.screener.length && (
                        <div className="mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full text-[10px] font-bold border border-orange-100">
                            <AlertCircle size={10} /> A few more on this
                        </div>
                    )}

                    <h2 className="text-base md:text-2xl font-black text-slate-800 mb-4 leading-snug min-h-[2.5rem]">
                        {currentQuestion.text}
                    </h2>
                    {currentQuestion.type === 'drag_sort' && (
                        // A config can word its own ends of the list (`sortHint`): a youth
                        // set ranks "strongest" to "most want to work on", not to "weakest".
                        <p className="text-sm text-slate-500 -mt-2 mb-4">{currentQuestion.sortHint || 'Drag to sort, strongest at the top and weakest at the bottom.'}</p>
                    )}

                    {isButtonGrid && (
                        <div className="grid grid-cols-1 gap-2 pb-2">
                            {getLikertOptions(currentQuestion).map((label, idx) => {
                                let val;
                                if (currentQuestion.type === 'select') {
                                    val = label;
                                } else if (currentQuestion.useManualScoring && currentQuestion.manualValues) {
                                    val = currentQuestion.manualValues[label] !== undefined ? currentQuestion.manualValues[label] : idx;
                                } else {
                                    val = idx;
                                }

                                const isSelected = answers[currentQuestion.id] === val;
                                return (
                                    <button 
                                        key={idx} 
                                        onClick={() => answerAndAdvance(currentQuestion, val)}
                                        className={`p-3 rounded-lg text-left font-bold border transition-all text-xs md:text-sm flex justify-between items-center ${isSelected ? 'bg-brand-primary border-brand-primary text-white shadow-md ring-2 ring-blue-200' : 'bg-slate-50 border-slate-100 text-slate-600 hover:bg-white hover:border-brand-primary/50'}`}
                                    >
                                        <span className="truncate pr-2">{label}</span>
                                        {isSelected
                                            ? <div className="bg-white/20 p-0.5 rounded-full flex-shrink-0"><ChevronRight size={12}/></div>
                                            : <span className="hidden md:inline text-[10px] font-semibold text-slate-300 flex-shrink-0">{idx + 1}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {currentQuestion.type === 'yes_no' && (
                        <div className="grid grid-cols-2 gap-3 pb-2">
                            {['Yes', 'No'].map((opt) => {
                                let val;
                                if (currentQuestion.useManualScoring && currentQuestion.manualValues) {
                                    val = currentQuestion.manualValues[opt] !== undefined ? currentQuestion.manualValues[opt] : (opt === 'Yes' ? 1 : 0);
                                } else {
                                    val = opt === 'Yes' ? 1 : 0;
                                }

                                const isSelected = answers[currentQuestion.id] === val;
                                return (
                                    <button 
                                        key={opt} 
                                        onClick={() => answerAndAdvance(currentQuestion, val)}
                                        className={`py-3 rounded-lg font-bold text-base border-2 transition-all ${isSelected ? 'bg-brand-primary border-brand-primary text-white shadow-md' : 'bg-white border-slate-100 text-slate-400 hover:border-brand-primary hover:text-brand-primary'}`}
                                    >
                                        {opt}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {currentQuestion.type === 'slider_100' && (
                        <div className="px-2 py-4">
                            <div className="text-center mb-4">
                                <span className={`text-3xl font-black ${getSliderLabel(sliderValue, currentQuestion).color}`}>
                                    {sliderValue}
                                </span>
                                <p className={`text-[10px] font-bold uppercase tracking-widest mt-1 ${getSliderLabel(sliderValue, currentQuestion).color}`}>
                                    {getSliderLabel(sliderValue, currentQuestion).text}
                                </p>
                            </div>

                            <input 
                                type="range" min="0" max="100" 
                                className="w-full h-4 rounded-full appearance-none cursor-pointer mb-4"
                                style={{ background: 'linear-gradient(to right, #ef4444 0%, #eab308 50%, #22c55e 100%)' }}
                                value={sliderValue}
                                onChange={(e) => handleAnswer(currentQuestion.id, parseInt(e.target.value), currentQuestion.trigger)}
                            />
                             <div className="flex justify-between text-[10px] font-bold uppercase text-slate-400">
                                <span>{rangeLabelsFor(currentQuestion)[0]}</span>
                                <span>{rangeLabelsFor(currentQuestion)[3]}</span>
                            </div>
                        </div>
                    )}

                    {currentQuestion.type === 'drag_sort' && (
                        <DragDropContext onDragEnd={handleOnDragEnd}>
                            <Droppable droppableId={currentQuestion.id}>
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2 pb-2">
                                        {(sortableLists[currentQuestion.id] || []).map((item, index) => (
                                            <Draggable key={item} draggableId={item} index={index}>
                                                {(provided, snapshot) => (
                                                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className={`p-3 bg-white border rounded-lg flex items-center gap-3 ${snapshot.isDragging ? 'shadow-lg border-brand-primary' : 'border-slate-200'}`}>
                                                        <GripVertical size={18} className="text-slate-300" />
                                                        <span className="font-bold text-sm text-slate-700">{index + 1}. {item}</span>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}

                     {(currentQuestion.type === 'text' || currentQuestion.type === 'number') && (
                        <input 
                            type={currentQuestion.type}
                            className="w-full p-3 text-base font-medium bg-slate-50 border-2 border-slate-100 rounded-lg focus:border-brand-primary focus:bg-white outline-none transition-all mb-4"
                            placeholder="Type here..."
                            value={answers[currentQuestion.id] || ''}
                            onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                        />
                    )}

                    {pendingAdvance && pendingAdvance.id === currentQuestion.id && !isLast && (
                        <div className="mt-3 animate-in fade-in duration-200">
                            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[var(--brand-accent)] rounded-full" style={{ animation: `advance ${ADVANCE_MS}ms linear forwards` }}></div>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1.5 text-center">Moving on. Tap your answer again to stay.</p>
                        </div>
                    )}
                    <style>{`@keyframes advance { from { width: 0 } to { width: 100% } }`}</style>
                </div>

                <div className="p-4 bg-white border-t border-slate-100 flex justify-between items-center shrink-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                    <button onClick={goBack} disabled={currentFlatIndex === 0} className={`flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-slate-400 hover:text-slate-600 transition-colors text-xs uppercase tracking-wider ${currentFlatIndex === 0 ? 'opacity-0 pointer-events-none' : ''}`}>
                        <ChevronLeft size={14} /> Back
                    </button>
                    {(isButtonGrid || currentQuestion.type === 'yes_no') && !isLast && (
                        <span className="hidden md:inline text-[11px] text-slate-400">Tap an answer, or press 1 to {optionLabels(currentQuestion).length}</span>
                    )}

                    {currentFlatIndex < flatQuestionList.length - 1 ? (
                        <button onClick={handleNext} disabled={!isCurrentQuestionAnswered()} className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                            Next <ChevronRight size={16} />
                        </button>
                    ) : (
                        <button onClick={handleSubmit} disabled={isSubmitting || !isCurrentQuestionAnswered()} className="flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-xl font-bold hover:brightness-110 transition-all shadow-md text-sm disabled:opacity-50">
                            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Complete
                        </button>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

export default AssessmentEngine;